/*
 *  Copyright © 2025 Cenozon Inc. All Rights Reserved.
 */
import {
   FETCH_JOINT_MATCHING_DATA_REQUEST,
   FETCH_JOINT_MATCHING_DATA_SUCCESS,
   FETCH_JOINT_MATCHING_DATA_FAILURE,
   FETCH_RUN_SUMMARY_REQUEST,
   FETCH_RUN_SUMMARY_SUCCESS,
   FETCH_RUN_SUMMARY_FAILURE,
   UPDATE_JOINT_MATCHING_FILTERS,
   CLEAR_JOINT_MATCHING_FILTERS,
   MATCH_JOINTS_REQUEST,
   MATCH_JOINTS_SUCCESS,
   MATCH_JOINTS_FAILURE,
   UNMATCH_JOINTS_REQUEST,
   UNMATCH_JOINTS_SUCCESS,
   UNMATCH_JOINTS_FAILURE,
   MATCH_MULTIPLE_JOINTS_REQUEST,
   MATCH_MULTIPLE_JOINTS_SUCCESS,
   MATCH_MULTIPLE_JOINTS_FAILURE,
   UNMATCH_MULTIPLE_JOINTS_REQUEST,
   UNMATCH_MULTIPLE_JOINTS_SUCCESS,
   UNMATCH_MULTIPLE_JOINTS_FAILURE,
   PROCESS_FEATURES_REQUEST,
   PROCESS_FEATURES_SUCCESS,
   PROCESS_FEATURES_FAILURE,
   PROCESS_TEMP_JOINTS_REQUEST,
   PROCESS_TEMP_JOINTS_SUCCESS,
   PROCESS_TEMP_JOINTS_FAILURE,
   SAVE_TEMP_JOINTS_REQUEST,
   SAVE_TEMP_JOINTS_SUCCESS,
   SAVE_TEMP_JOINTS_FAILURE,
   PROCESS_JOINTS_MATCHING_REQUEST,
   PROCESS_JOINTS_MATCHING_SUCCESS,
   PROCESS_JOINTS_MATCHING_FAILURE,
   SET_SELECTED_JOINTS,
   CLEAR_SELECTED_JOINTS,
   // Run Search Action Types
   FETCH_RUN_SEARCH_FIELDS_REQUEST,
   FETCH_RUN_SEARCH_FIELDS_SUCCESS,
   FETCH_RUN_SEARCH_FIELDS_FAILURE,
   FETCH_RUN_SEARCH_RESULTS_REQUEST,
   FETCH_RUN_SEARCH_RESULTS_SUCCESS,
   FETCH_RUN_SEARCH_RESULTS_FAILURE,
   UPDATE_RUN_SEARCH_FILTERS,
   CLEAR_RUN_SEARCH_FILTERS,
   UPDATE_RUN_SEARCH_UI_STATE,
   SET_SELECTED_RUNS,
   CHECK_TEMP_JOINTS_EXISTENCE_REQUEST,
   CHECK_TEMP_JOINTS_EXISTENCE_SUCCESS,
   CHECK_TEMP_JOINTS_EXISTENCE_FAILURE
} from '../action/runToRunMatchingAction';

// A response is stale once a newer request for the same data has been dispatched.
const isStaleResponse = (action, state, requestIdKey) => action.requestId !== state[requestIdKey];

const initialState = {
   // Data
   jointMatchingData: [],
   filteredData: [],
   runSummary: null,
   
   // Loading states
   isLoading: false,
   isSummaryLoading: false,
   isMatching: false,
   isUnmatching: false,
   isMatchingMultiple: false,
   isUnmatchingMultiple: false,
   isProcessingFeatures: false,
   isProcessingTempJoints: false,
   isSavingTempJoints: false,
   isProcessingJointsMatching: false,
   isCheckingTempJoints: false,
   
   // Temp joints existence
   hasTempJoints: false,
   
   // Error states
   error: null,
   summaryError: null,
   matchError: null,
   unmatchError: null,
   matchMultipleError: null,
   unmatchMultipleError: null,
   processFeaturesError: null,
   processTempJointsError: null,
   saveTempJointsError: null,
   processJointsMatchingError: null,
   checkTempJointsExistenceError: null,
   
   // Filters
   filters: {
      jointNumber: '',
      minDiff: '',
      maxDiff: '',
      iterations: '',
      status: ''
   },
   
   // Selection
   selectedMasterJoint: null,
   selectedTargetJoint: null,
   
   // UI state
   syncScrollEnabled: false,
   expandedRows: new Set(),
   
   // Run Search State
   runSearchFields: {
      operationalAreas: [],
      vendors: [],
      projectNames: [],
      routes: [],
      toolTypes: []
   },
   runSearchResults: {
      results: [],
      __count: 0
   },
   runSearchFilters: {
      operationalAreas: [],
      vendors: [],
      projectNames: [],
      routes: [],
      toolTypes: []
   },
   selectedRuns: {
      masterRun: null,
      targetRun: null
   },
   runSearchUIState: {
      page: 0,
      rowsPerPage: 50,
      orderBy: 'routeName',
      isAsc: true,
      totalCount: 0,
      isSearchDataReceived: false
   },
   isRunSearchFieldsLoading: false,
   isRunSearchResultsLoading: false,
   runSearchFieldsError: null,
   runSearchResultsError: null,
   latestRunSearchRequestId: 0,

   latestJointMatchingRunSid: null,
   latestJointMatchingRequestId: 0,
   latestRunSummaryRunSid: null,
   latestRunSummaryRequestId: 0,
   latestTempJointsRunSid: null,
   latestTempJointsRequestId: 0
};

const runToRunMatchingReducer = (state = initialState, action) => {
   switch (action.type) {
      case FETCH_JOINT_MATCHING_DATA_REQUEST: {
         // New run: clear the previous run's grid data instead of leaving it displayed
         const isNewRun = action.runSid !== state.latestJointMatchingRunSid;
         return {
            ...state,
            isLoading: true,
            error: null,
            jointMatchingData: isNewRun ? [] : state.jointMatchingData,
            filteredData: isNewRun ? [] : state.filteredData,
            latestJointMatchingRunSid: action.runSid,
            latestJointMatchingRequestId: action.requestId
         };
      }

      case FETCH_JOINT_MATCHING_DATA_SUCCESS:
         if (isStaleResponse(action, state, 'latestJointMatchingRequestId')) {
            return state;
         }
         return {
            ...state,
            isLoading: false,
            jointMatchingData: action.payload,
            filteredData: action.payload,
            error: null
         };

      case FETCH_JOINT_MATCHING_DATA_FAILURE:
         if (isStaleResponse(action, state, 'latestJointMatchingRequestId')) {
            return state;
         }
         return {
            ...state,
            isLoading: false,
            error: action.payload
         };

      case FETCH_RUN_SUMMARY_REQUEST: {
         // New run: clear the previous run's summary instead of leaving it displayed
         const isNewRun = action.runSid !== state.latestRunSummaryRunSid;
         return {
            ...state,
            isSummaryLoading: true,
            summaryError: null,
            runSummary: isNewRun ? null : state.runSummary,
            latestRunSummaryRunSid: action.runSid,
            latestRunSummaryRequestId: action.requestId
         };
      }

      case FETCH_RUN_SUMMARY_SUCCESS:
         if (isStaleResponse(action, state, 'latestRunSummaryRequestId')) {
            return state;
         }
         return {
            ...state,
            isSummaryLoading: false,
            runSummary: action.payload,
            summaryError: null
         };

      case FETCH_RUN_SUMMARY_FAILURE:
         if (isStaleResponse(action, state, 'latestRunSummaryRequestId')) {
            return state;
         }
         return {
            ...state,
            isSummaryLoading: false,
            summaryError: action.payload
         };
         
      case UPDATE_JOINT_MATCHING_FILTERS:
         return {
            ...state,
            filters: {
               ...state.filters,
               ...action.payload
            }
         };
         
      case CLEAR_JOINT_MATCHING_FILTERS:
         return {
            ...state,
            filters: initialState.filters,
            filteredData: state.jointMatchingData
         };
         
      case MATCH_JOINTS_REQUEST:
         return {
            ...state,
            isMatching: true,
            matchError: null
         };
         
      case MATCH_JOINTS_SUCCESS:
         return {
            ...state,
            isMatching: false,
            jointMatchingData: action.payload,
            filteredData: action.payload,
            matchError: null
         };
         
      case MATCH_JOINTS_FAILURE:
         return {
            ...state,
            isMatching: false,
            matchError: action.payload
         };
         
      case UNMATCH_JOINTS_REQUEST:
         return {
            ...state,
            isUnmatching: true,
            unmatchError: null
         };
         
      case UNMATCH_JOINTS_SUCCESS:
         return {
            ...state,
            isUnmatching: false,
            jointMatchingData: action.payload,
            filteredData: action.payload,
            unmatchError: null
         };
         
      case UNMATCH_JOINTS_FAILURE:
         return {
            ...state,
            isUnmatching: false,
            unmatchError: action.payload
         };
         
      case MATCH_MULTIPLE_JOINTS_REQUEST:
         return {
            ...state,
            isMatchingMultiple: true,
            matchMultipleError: null
         };
         
      case MATCH_MULTIPLE_JOINTS_SUCCESS:
         return {
            ...state,
            isMatchingMultiple: false,
            jointMatchingData: action.payload,
            filteredData: action.payload,
            matchMultipleError: null
         };
         
      case MATCH_MULTIPLE_JOINTS_FAILURE:
         return {
            ...state,
            isMatchingMultiple: false,
            matchMultipleError: action.payload
         };
         
      case UNMATCH_MULTIPLE_JOINTS_REQUEST:
         return {
            ...state,
            isUnmatchingMultiple: true,
            unmatchMultipleError: null
         };
         
      case UNMATCH_MULTIPLE_JOINTS_SUCCESS:
         return {
            ...state,
            isUnmatchingMultiple: false,
            jointMatchingData: action.payload,
            filteredData: action.payload,
            unmatchMultipleError: null
         };
         
      case UNMATCH_MULTIPLE_JOINTS_FAILURE:
         return {
            ...state,
            isUnmatchingMultiple: false,
            unmatchMultipleError: action.payload
         };
         
      case PROCESS_FEATURES_REQUEST:
         return {
            ...state,
            isProcessingFeatures: true,
            processFeaturesError: null
         };
         
      case PROCESS_FEATURES_SUCCESS:
         return {
            ...state,
            isProcessingFeatures: false,
            processFeaturesError: null
         };
         
      case PROCESS_FEATURES_FAILURE:
         return {
            ...state,
            isProcessingFeatures: false,
            processFeaturesError: action.payload
         };
         
      case PROCESS_TEMP_JOINTS_REQUEST:
         return {
            ...state,
            isProcessingTempJoints: true,
            processTempJointsError: null
         };
         
      case PROCESS_TEMP_JOINTS_SUCCESS:
         return {
            ...state,
            isProcessingTempJoints: false,
            hasTempJoints: false, // Set to false when temp joints are processed (removed)
            processTempJointsError: null
         };
         
      case PROCESS_TEMP_JOINTS_FAILURE:
         return {
            ...state,
            isProcessingTempJoints: false,
            processTempJointsError: action.payload
         };
         
      case SAVE_TEMP_JOINTS_REQUEST:
         return {
            ...state,
            isSavingTempJoints: true,
            saveTempJointsError: null
         };
         
      case SAVE_TEMP_JOINTS_SUCCESS:
         return {
            ...state,
            isSavingTempJoints: false,
            hasTempJoints: true, // Immediately set to true when temp joints are saved
            saveTempJointsError: null
         };
         
      case SAVE_TEMP_JOINTS_FAILURE:
         return {
            ...state,
            isSavingTempJoints: false,
            saveTempJointsError: action.payload
         };
         
      case SET_SELECTED_JOINTS:
         return {
            ...state,
            selectedMasterJoint: action.payload.masterJoint,
            selectedTargetJoint: action.payload.targetJoint
         };
         
      case CLEAR_SELECTED_JOINTS:
         return {
            ...state,
            selectedMasterJoint: null,
            selectedTargetJoint: null
         };

      case PROCESS_JOINTS_MATCHING_REQUEST:
         return {
            ...state,
            isProcessingJointsMatching: true,
            processJointsMatchingError: null
         };

      case PROCESS_JOINTS_MATCHING_SUCCESS:
         return {
            ...state,
            isProcessingJointsMatching: false,
            processJointsMatchingError: null
         };

      case PROCESS_JOINTS_MATCHING_FAILURE:
         return {
            ...state,
            isProcessingJointsMatching: false,
            processJointsMatchingError: action.payload
         };

      // Run Search Cases
      case FETCH_RUN_SEARCH_FIELDS_REQUEST:
         return {
            ...state,
            isRunSearchFieldsLoading: true,
            runSearchFieldsError: null
         };

      case FETCH_RUN_SEARCH_FIELDS_SUCCESS:
         return {
            ...state,
            isRunSearchFieldsLoading: false,
            runSearchFields: action.payload,
            runSearchFieldsError: null
         };

      case FETCH_RUN_SEARCH_FIELDS_FAILURE:
         return {
            ...state,
            isRunSearchFieldsLoading: false,
            runSearchFieldsError: action.payload
         };

      case FETCH_RUN_SEARCH_RESULTS_REQUEST:
         return {
            ...state,
            isRunSearchResultsLoading: true,
            runSearchResultsError: null,
            latestRunSearchRequestId: action.requestId
         };

      case FETCH_RUN_SEARCH_RESULTS_SUCCESS:
         if (isStaleResponse(action, state, 'latestRunSearchRequestId')) {
            return state;
         }
         return {
            ...state,
            isRunSearchResultsLoading: false,
            runSearchResults: action.payload,
            runSearchResultsError: null
         };

      case FETCH_RUN_SEARCH_RESULTS_FAILURE:
         if (isStaleResponse(action, state, 'latestRunSearchRequestId')) {
            return state;
         }
         return {
            ...state,
            isRunSearchResultsLoading: false,
            runSearchResultsError: action.payload
         };

      case UPDATE_RUN_SEARCH_FILTERS:
         return {
            ...state,
            runSearchFilters: {
               ...state.runSearchFilters,
               ...action.payload
            }
         };

      case CLEAR_RUN_SEARCH_FILTERS:
         return {
            ...state,
            runSearchFilters: initialState.runSearchFilters
         };

      case UPDATE_RUN_SEARCH_UI_STATE:
         return {
            ...state,
            runSearchUIState: {
               ...state.runSearchUIState,
               ...action.payload
            }
         };

      case SET_SELECTED_RUNS:
         return {
            ...state,
            selectedRuns: {
               masterRun: action.payload.masterRun,
               targetRun: action.payload.targetRun
            }
         };

      case CHECK_TEMP_JOINTS_EXISTENCE_REQUEST: {
         // New run: clear the previous run's flag instead of leaving it displayed
         const isNewRun = action.runSid !== state.latestTempJointsRunSid;
         return {
            ...state,
            isCheckingTempJoints: true,
            checkTempJointsExistenceError: null,
            hasTempJoints: isNewRun ? false : state.hasTempJoints,
            latestTempJointsRunSid: action.runSid,
            latestTempJointsRequestId: action.requestId
         };
      }

      case CHECK_TEMP_JOINTS_EXISTENCE_SUCCESS:
         if (isStaleResponse(action, state, 'latestTempJointsRequestId')) {
            return state;
         }
         return {
            ...state,
            isCheckingTempJoints: false,
            hasTempJoints: action.payload,
            checkTempJointsExistenceError: null
         };

      case CHECK_TEMP_JOINTS_EXISTENCE_FAILURE:
         if (isStaleResponse(action, state, 'latestTempJointsRequestId')) {
            return state;
         }
         return {
            ...state,
            isCheckingTempJoints: false,
            checkTempJointsExistenceError: action.payload
         };
          
      default:
         return state;
   }
};

export default runToRunMatchingReducer;
